import React, { useState } from 'react';
import { Card, Flex, Text, Button, SearchField } from '@aws-amplify/ui-react';
import MeetingCard from './MeetingCard';
import DatePicker from './DatePicker';
import FileUpload from './FileUpload';

function Sidebar({
  items,
  onUploadComplete,
  onItemSelect,
  hasMoreItems,
  onLoadMore,
  identityId,
  selectedItem,
  onDeleteItem,
  selectedDate,
  onDateChange
}) {
  const [searchTerm, setSearchTerm] = useState('');

  const filterItems = () => {
    return items.filter(item => !item.object_name.endsWith('/'));
  };

  const searchFilteredItems = (items) => {
    if (!searchTerm.trim()) return items;
    return items.filter(item =>
      item.object_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const groupItemsByDate = (items) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      earlier: []
    };

    items.forEach(item => {
      const itemDate = new Date(item.last_updated);
      const itemDay = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());

      if (itemDay.getTime() === today.getTime()) {
        groups.today.push(item);
      } else if (itemDay.getTime() === yesterday.getTime()) {
        groups.yesterday.push(item);
      } else if (itemDate >= weekAgo) {
        groups.thisWeek.push(item);
      } else {
        groups.earlier.push(item);
      }
    });

    return groups;
  };

  const filteredItems = filterItems();
  const searchedItems = searchFilteredItems(filteredItems);
  const groupedItems = groupItemsByDate(searchedItems);

  return (
    <Card 
      padding="0" 
      height="100%" 
      style={{ 
        display: 'flex', 
        flexDirection: 'column',
        borderRight: '1px solid #e0e0e0'
      }}
    >
      <Flex direction="column" padding="0.75rem" borderBottom="1px solid #e0e0e0">
        <Text fontSize="1rem" fontWeight="bold" marginBottom="0.75rem">Meetings</Text>
        <FileUpload 
          selectedDate={selectedDate}
          identityId={identityId}
          onUploadComplete={onUploadComplete}
        />
      </Flex>

      <Flex direction="column" padding="0.5rem" gap="0.5rem" borderBottom="1px solid #e0e0e0">
        <DatePicker
          selectedDate={selectedDate}
          onDateChange={onDateChange}
        />
        <SearchField
          label=""
          placeholder="Search meetings..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm('')}
          hasSearchButton={false}
          hasSearchIcon={true}
          size="small"
        />
      </Flex>

      <div style={{ flex: 1, overflow: 'auto', padding: '0.5rem' }}>
        {filteredItems.length === 0 ? (
          <Flex direction="column" alignItems="center" gap="0.5rem" padding="2rem 1rem" style={{ textAlign: 'center' }}>
            <Text fontSize="2rem">📁</Text>
            <Text fontWeight="600">No meetings yet</Text>
            <Text fontSize="0.8rem" color="gray">Upload an audio file to get started</Text>
          </Flex>
        ) : searchedItems.length === 0 ? (
          <Flex direction="column" alignItems="center" gap="0.5rem" padding="2rem 1rem" style={{ textAlign: 'center' }}>
            <Text fontSize="2rem">🔍</Text>
            <Text fontWeight="600">No matches found</Text>
            <Text fontSize="0.8rem" color="gray">Try a different search term</Text>
            <Button size="small" onClick={() => setSearchTerm('')} variation="link">Clear search</Button>
          </Flex>
        ) : (
          <Flex direction="column" gap="1rem">
            {groupedItems.today.length > 0 && (
              <div>
                <Text fontSize="0.7rem" fontWeight="bold" color="gray" marginBottom="0.5rem" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Today
                </Text>
                <Flex direction="column" gap="0.5rem">
                  {groupedItems.today.map((item) => (
                    <MeetingCard
                      key={item.key}
                      item={item}
                      isSelected={selectedItem && selectedItem.key === item.key}
                      onClick={() => onItemSelect(item)}
                      onDelete={onDeleteItem}
                    />
                  ))}
                </Flex>
              </div>
            )}

            {groupedItems.yesterday.length > 0 && (
              <div>
                <Text fontSize="0.7rem" fontWeight="bold" color="gray" marginBottom="0.5rem" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Yesterday
                </Text>
                <Flex direction="column" gap="0.5rem">
                  {groupedItems.yesterday.map((item) => (
                    <MeetingCard
                      key={item.key}
                      item={item}
                      isSelected={selectedItem && selectedItem.key === item.key}
                      onClick={() => onItemSelect(item)}
                      onDelete={onDeleteItem}
                    />
                  ))}
                </Flex>
              </div>
            )}

            {groupedItems.thisWeek.length > 0 && (
              <div>
                <Text fontSize="0.7rem" fontWeight="bold" color="gray" marginBottom="0.5rem" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  This Week
                </Text>
                <Flex direction="column" gap="0.5rem">
                  {groupedItems.thisWeek.map((item) => (
                    <MeetingCard
                      key={item.key}
                      item={item}
                      isSelected={selectedItem && selectedItem.key === item.key}
                      onClick={() => onItemSelect(item)}
                      onDelete={onDeleteItem}
                    />
                  ))}
                </Flex>
              </div>
            )}

            {groupedItems.earlier.length > 0 && (
              <div>
                <Text fontSize="0.7rem" fontWeight="bold" color="gray" marginBottom="0.5rem" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Earlier
                </Text>
                <Flex direction="column" gap="0.5rem">
                  {groupedItems.earlier.map((item) => (
                    <MeetingCard
                      key={item.key}
                      item={item}
                      isSelected={selectedItem && selectedItem.key === item.key}
                      onClick={() => onItemSelect(item)}
                      onDelete={onDeleteItem}
                    />
                  ))}
                </Flex>
              </div>
            )}
          </Flex>
        )}
        
        {hasMoreItems && (
          <Button 
            onClick={onLoadMore} 
            style={{ 
              marginTop: '1rem',
              width: '100%'
            }}
            variation="primary"
          >
            Load More
          </Button>
        )}
      </div>
    </Card>
  );
}

export default Sidebar;